<template>
  <div class="login-shell">
    <div class="login-backdrop" aria-hidden="true" />

    <div class="login-lang-corner">
      <button
        type="button"
        class="login-theme-trigger"
        :title="themeToggleTitle"
        :aria-label="themeToggleTitle"
        @click="toggleUiTheme"
      >
        <i :class="themeIconClass" aria-hidden="true" />
      </button>
      <el-dropdown
        trigger="click"
        popper-class="login-lang-dropdown-popper"
        @command="handleLangCommand"
      >
        <button type="button" class="login-lang-trigger">
          <span class="login-lang-flag">{{ currentLanguageFlag }}</span>
          <span class="d-none d-sm-inline login-lang-name">{{
            currentLanguageName
          }}</span>
          <i class="fas fa-chevron-down login-lang-chevron" aria-hidden="true" />
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              v-for="lang in languages"
              :key="lang.code"
              :command="'lang-' + lang.code">
              <span class="me-2">{{ lang.flag }}</span>{{ $t(lang.nameKey) }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div class="login-center">
      <div class="login-card">
        <div class="text-center login-logo-wrap">
          <img class="login-logo" :src="logo" alt="" />
        </div>

        <h1 class="login-title">{{ $t("welcome") }}</h1>
        <p class="login-subtitle">{{ $t("enterAccount") }}</p>

        <form class="login-form" @submit.prevent="signIn">
          <div class="mb-3">
            <label class="form-label login-label" for="login-user">{{
              $t("username")
            }}</label>
            <el-input
              id="login-user"
              v-model="log"
              class="login-field-el"
              placeholder=""
              size="large"
              style="width: 100%;"
              autocomplete="username"
            />
          </div>
          <div class="mb-3">
            <label class="form-label login-label" for="login-pass">{{
              $t("password")
            }}</label>
            <el-input
              id="login-pass"
              v-model="password"
              class="login-field-el"
              type="password"
              placeholder=""
              size="large"
              style="width: 100%;"
              autocomplete="current-password"
              @keyup.enter="signIn"
            />
          </div>
          <div class="recaptcha-wrap">
            <div v-if="showRecaptcha" class="recaptcha-inner">
              <vue-recaptcha
                ref="recaptchaRef"
                :key="recaptchaMountKey"
                :sitekey="recaptchaKey"
                :hl="recaptchaLocale"
                theme="light"
                size="normal"
                :loading-timeout="20000"
                @verify="recaptchaVerified"
                @expire="onRecaptchaExpire"
                @error="onRecaptchaError"
                @fail="onRecaptchaError"
              />
            </div>
            <button
              v-else-if="recaptchaLoadFailed"
              type="button"
              class="recaptcha-retry-btn"
              @click="mountRecaptcha"
            >
              {{ $t("btn_refresh") }}
            </button>
          </div>
          <div class="text-center login-submit-wrap">
            <input
              type="submit"
              :value="$t('submit')"
              class="mb-0 btn btn-md bg-gradient-success w-100"
              :disabled="loading"
            />
          </div>
          <div class="text-center logo2-wrap">
            <img class="pr-logo" :src="logo2" alt="" />
          </div>
          <p v-if="message" class="login-message">
            <i class="bi bi-exclamation-circle-fill" aria-hidden="true" />
            {{ message }}
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import logo2 from "@/assets/img/logos/progressico2.png";
import CryptoJS from "crypto-js";
import MedicalAgent from "../Source/medicalAgent";
import Utils from "@/project/Source/utils.js";
import { imgFile } from "@/project/Source/helper.js";
import vueRecaptcha from "vue3-recaptcha2";
import { recaptchaKey, ensureRecaptchaApi } from "@/project/Source/helper";
import { changeLanguage } from "@/project/Source/lang/i18n";
import {
  getAgentTheme,
  toggleAgentTheme,
  isAgentDarkTheme,
} from "@/project/Source/agentTheme.js";

/** После входа — каталог продуктов агента. */
const POST_LOGIN_ROUTE = "/catalog";

/**
 * true — мок-вход без Medical Agent API (офлайн-вёрстка).
 * false — SystemAuth/Login на medical-agent-api + UserBranch / UserByRole в session storage.
 */
const SKIP_LOGIN_CHECK = false;

function fakeLoginResponse(username) {
  const name = (username && String(username).trim()) || "demo";
  return {
    data: {
      isSuccess: true,
      errors: [],
      data: [
        {
          jwt: `mock-jwt-${Date.now()}`,
          roles: [{ roleId: 55, roleName: "MedicalDevMock" }],
          user: {
            fullName: `${name} (mock)`,
            companyName: "Dev · без API",
          },
        },
      ],
    },
  };
}

export default {
  name: "Login",
  components: {
    vueRecaptcha,
  },
  data() {
    return {
      log: "",
      logo: require(`@/assets/img/logos/${imgFile}/progressico.png`),
      logo2,
      password: "",
      message: null,
      recaptchaKey,
      showRecaptcha: false,
      recaptchaMountKey: 0,
      recaptchaLoadFailed: false,
      isCaptchaValid: false,
      loading: false,
      uiTheme: getAgentTheme(),
      languages: [
        { code: "az", nameKey: "languageAz", flag: "🇦🇿" },
        { code: "en", nameKey: "languageEn", flag: "🇺🇸" },
        { code: "ru", nameKey: "languageRu", flag: "🇷🇺" },
        { code: "tr", nameKey: "languageTr", flag: "🇹🇷" },
      ],
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale?.value || this.$i18n.locale || "en";
    },
    currentLanguageName() {
      const lang = this.languages.find((l) => l.code === this.currentLocale);
      return lang ? this.$t(lang.nameKey) : this.$t("languageEn");
    },
    currentLanguageFlag() {
      const lang = this.languages.find((l) => l.code === this.currentLocale);
      return lang ? lang.flag : "🇺🇸";
    },
    isDarkUiTheme() {
      return isAgentDarkTheme(this.uiTheme);
    },
    themeIconClass() {
      return this.isDarkUiTheme ? "fas fa-sun" : "fas fa-moon";
    },
    themeToggleTitle() {
      return this.isDarkUiTheme
        ? this.$t("agentThemeSwitchLight")
        : this.$t("agentThemeSwitchDark");
    },
    recaptchaLocale() {
      const code = this.currentLocale;
      return code === "az" ? "az" : code === "ru" ? "ru" : code === "tr" ? "tr" : "en";
    },
  },
  watch: {
    "$i18n.locale.value"() {
      this.$forceUpdate();
      this.remountRecaptcha();
    },
    "$i18n.locale"() {
      this.$forceUpdate();
      this.remountRecaptcha();
    },
  },
  mounted() {
    this.uiTheme = getAgentTheme();
    this._themeListener = (e) => {
      if (e?.detail?.theme) this.uiTheme = e.detail.theme;
    };
    window.addEventListener("agent-theme-changed", this._themeListener);
    this.mountRecaptcha();
  },
  beforeUnmount() {
    if (this._themeListener) {
      window.removeEventListener("agent-theme-changed", this._themeListener);
    }
  },
  methods: {
    toggleUiTheme() {
      this.uiTheme = toggleAgentTheme();
    },
    handleLangCommand(command) {
      if (command && command.startsWith("lang-")) {
        const code = command.replace("lang-", "");
        changeLanguage(code);
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    mountRecaptcha() {
      this.showRecaptcha = false;
      this.recaptchaLoadFailed = false;
      this.isCaptchaValid = false;
      ensureRecaptchaApi(this.recaptchaLocale)
        .then(() => {
          this.recaptchaMountKey += 1;
          this.showRecaptcha = true;
        })
        .catch(() => {
          this.recaptchaLoadFailed = true;
        });
    },
    remountRecaptcha() {
      this.isCaptchaValid = false;
      this.mountRecaptcha();
    },
    recaptchaVerified() {
      this.isCaptchaValid = true;
    },
    onRecaptchaExpire() {
      this.isCaptchaValid = false;
      this.$refs.recaptchaRef?.reset?.();
    },
    onRecaptchaError() {
      this.isCaptchaValid = false;
      this.remountRecaptcha();
    },
    /** Обработка ответа SystemAuth/Login (как у axios после успешного запроса). */
    applyLoginResponse(response) {
      if (!response || !response.data) {
        this.loading = false;
        this.message = this.$t("error");
        return;
      }

      const body = response.data;
      // Prod API may omit isSuccess; only treat explicit false as failure.
      if (body.isSuccess === false) {
        this.loading = false;
        const err =
          (Array.isArray(body.errors) && body.errors[0]) ||
          body.message ||
          this.$t("error");
        this.message = typeof err === "string" ? err : this.$t("error");
        return;
      }

      try {
        const row = (body.data && body.data[0]) || body.data || null;
        if (!row || !row.user) {
          this.loading = false;
          this.message = this.$t("error");
          console.error("[Login] unexpected response shape", body);
          return;
        }

        const jwtToken =
          row.jwt ||
          row.token ||
          body.jwt ||
          body.token ||
          row.user?.jwt ||
          row.user?.token;
        if (!jwtToken || String(jwtToken).trim().toLowerCase() === "none") {
          this.loading = false;
          this.message = this.$t("error");
          console.error("[Login] missing jwt in response", body);
          return;
        }

        window.localStorage.setItem(
          "jwt",
          CryptoJS.AES.encrypt(JSON.stringify(jwtToken), "secret_key").toString()
        );
        if (row.roles) {
          window.localStorage.setItem(
            "roles",
            CryptoJS.AES.encrypt(JSON.stringify(row.roles), "secret_key").toString()
          );
        }
        window.localStorage.setItem(
          "user",
          CryptoJS.AES.encrypt(JSON.stringify(row.user), "secret_key").toString()
        );
        sessionStorage.setItem("policy", JSON.stringify("none"));

        window.dispatchEvent(new CustomEvent("progress-auth-updated"));

        const pathParts = this.$route.path.split("/").filter(Boolean);
        const target =
          pathParts.length >= 2
            ? "/" + pathParts.slice(1).join("/")
            : POST_LOGIN_ROUTE;

        const finishNavigation = () => {
          this.$router
            .push(target)
            .then(() => {
              this.loading = false;
            })
            .catch((err) => {
              if (err && err.name === "NavigationDuplicated") {
                this.loading = false;
                return;
              }
              this.loading = false;
              this.message = this.$t("error");
            });
        };

        MedicalAgent.persistBranchAndRoleAfterLogin(row)
          .then(() => finishNavigation())
          .catch(() => finishNavigation());
      } catch (e) {
        console.error(e);
        this.loading = false;
        this.message = this.$t("error");
      }
    },
    signIn(event) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }

      this.loading = true;
      this.message = null;

      const data = {
        requestCorrelationId: crypto.randomUUID(),
        username: this.log,
        password: this.password,
      };

      if (SKIP_LOGIN_CHECK) {
        this.applyLoginResponse(fakeLoginResponse(this.log));
        return;
      }

      console.log("[Login] POST", MedicalAgent.medicalAgentApiBase + "/SystemAuth/Login", {
        headers: { "X-API-Key": "(set)", "Content-Type": "application/json" },
        body: { ...data, password: "***" },
      });

      MedicalAgent.Login(data)
        .then((response) => {
          console.log("[Login] response", response?.status, response?.data);
          this.applyLoginResponse(response);
        })
        .catch((err) => {
          console.error("[Login] failed", {
            url: MedicalAgent.medicalAgentApiBase,
            status: err?.response?.status,
            data: err?.response?.data,
            message: err?.message,
          });
          this.loading = false;
          this.message =
            Utils.FormatErrorWithCorrelationId(err) ||
            err?.message ||
            this.$t("error");
        });
    },
  },
};
</script>

<style scoped>
.login-shell {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  --login-pad: clamp(0.75rem, 4vw, 2rem);
  padding: var(--login-pad);
  padding-top: max(var(--login-pad), env(safe-area-inset-top, 0px));
  padding-right: max(var(--login-pad), env(safe-area-inset-right, 0px));
  padding-bottom: max(var(--login-pad), env(safe-area-inset-bottom, 0px));
  padding-left: max(var(--login-pad), env(safe-area-inset-left, 0px));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  color: #e8ecf4;
}

.login-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(1200px 800px at 20% 10%, rgba(99, 102, 241, 0.35), transparent 55%),
    radial-gradient(900px 600px at 85% 30%, rgba(14, 165, 233, 0.25), transparent 50%),
    radial-gradient(700px 500px at 50% 90%, rgba(236, 72, 153, 0.12), transparent 55%),
    linear-gradient(165deg, #070a12 0%, #0c1222 45%, #06080f 100%);
}

.login-backdrop::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
  opacity: 0.6;
  pointer-events: none;
}

.login-center {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: min(440px, 100%);
  margin-left: auto;
  margin-right: auto;
}

.login-card {
  width: 100%;
  box-sizing: border-box;
  padding: clamp(1.25rem, 5vw, 2.25rem);
  border-radius: clamp(14px, 3vw, 20px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(15, 20, 34, 0.72);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(18px);
}

.login-logo-wrap {
  margin-bottom: clamp(0.35rem, 2vw, 0.5rem);
}

.login-logo {
  width: auto;
  max-width: min(220px, 72vw);
  max-height: clamp(72px, 18vw, 120px);
  height: auto;
  object-fit: contain;
}

.login-title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.1rem, 4.2vw, 1.35rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  text-align: center;
  color: #e8ecf4;
  line-height: 1.25;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

.login-subtitle {
  margin: 0 0 clamp(0.9rem, 3vw, 1.25rem);
  font-size: clamp(0.8rem, 3vw, 0.9rem);
  text-align: center;
  color: rgba(232, 236, 244, 0.65);
  line-height: 1.45;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.login-label {
  color: rgba(232, 236, 244, 0.9);
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.35rem;
}

/* el-input on dark glass: only what the default theme does not provide */
.login-field-el :deep(.el-input__wrapper) {
  border-radius: 12px;
  background: rgba(6, 9, 18, 0.55) !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.14) inset !important;
}

.login-field-el :deep(.el-input__inner) {
  color: #f1f4fb !important;
}

.login-field-el :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.75) inset,
    0 0 0 3px rgba(99, 102, 241, 0.25) !important;
}

.login-submit-wrap {
  margin-top: 0.5rem;
}

.recaptcha-wrap {
  margin-top: clamp(0.35rem, 2vw, 0.75rem);
  width: 100%;
  display: flex;
  justify-content: center;
}

.recaptcha-inner {
  width: 304px;
  max-width: 100%;
  height: 78px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  line-height: 0;
}

.recaptcha-inner :deep(> div) {
  display: block !important;
  width: 304px !important;
  max-width: 100%;
  height: 78px !important;
  margin: 0 auto !important;
  overflow: hidden !important;
  transform: none !important;
}

.recaptcha-inner :deep(iframe) {
  display: block !important;
  margin: 0 auto !important;
  vertical-align: top;
}

.recaptcha-inner :deep(textarea) {
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

@media (max-width: 360px) {
  .recaptcha-inner {
    transform: scale(0.92);
    transform-origin: center top;
    height: 72px;
  }
}

.recaptcha-retry-btn {
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 0.45rem 0.9rem;
  background: rgba(255, 255, 255, 0.08);
  color: #e8ecf4;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.recaptcha-retry-btn:hover {
  background: rgba(255, 255, 255, 0.14);
}

.logo2-wrap {
  margin-top: 1rem;
}

.login-message {
  margin: 0.75rem 0 0;
  text-align: center;
  font-size: 0.85rem;
  color: #f87171;
}

.login-lang-corner {
  position: fixed;
  top: max(clamp(0.5rem, 2vw, 1rem), env(safe-area-inset-top, 0px));
  right: max(clamp(0.5rem, 2vw, 1rem), env(safe-area-inset-right, 0px));
  z-index: 2;
  max-width: calc(100vw - 1rem);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.login-theme-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border-radius: 12px;
  border: 1px solid var(--agent-nav-chip-border, rgba(255, 255, 255, 0.14));
  background: var(--agent-nav-chip-bg, rgba(8, 12, 24, 0.72));
  color: var(--agent-login-text, #e8ecf4);
  cursor: pointer;
  backdrop-filter: blur(14px);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.login-theme-trigger:hover {
  border-color: rgba(56, 189, 248, 0.45);
}

.login-lang-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 44px;
  padding: 0.45rem clamp(0.55rem, 2.5vw, 0.9rem);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(8, 12, 24, 0.72);
  backdrop-filter: blur(14px);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  color: #e8ecf4;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.login-lang-trigger:hover {
  border-color: rgba(255, 255, 255, 0.26);
  background: rgba(14, 20, 36, 0.9);
  color: #f8fafc;
}

.login-lang-trigger:focus {
  outline: none;
}

.login-lang-trigger:focus-visible {
  outline: 2px solid rgba(129, 140, 248, 0.75);
  outline-offset: 2px;
}

.login-lang-flag {
  font-size: 1.25rem;
  line-height: 1;
}

.login-lang-name {
  max-width: 9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-lang-chevron {
  font-size: 0.65rem;
  opacity: 0.8;
  margin-left: 0.1rem;
}

.pr-logo {
  width: clamp(120px, 40vw, 150px);
  max-width: 100%;
  height: auto;
}

/* Narrow phones: extra top space so card clears fixed language control */
@media (max-width: 575.98px) {
  .login-shell {
    padding-top: max(3.5rem, env(safe-area-inset-top, 0px), var(--login-pad));
    align-items: flex-start;
    padding-bottom: max(1.25rem, env(safe-area-inset-bottom, 0px));
  }

  .login-center {
    padding-top: 0.25rem;
  }
}

@media (min-width: 576px) {
  .login-shell {
    align-items: center;
  }
}

/* Compact landscape on short viewports */
@media (max-height: 520px) and (orientation: landscape) {
  .login-shell {
    align-items: flex-start;
    padding-top: max(3rem, env(safe-area-inset-top, 0px));
  }

  .login-card {
    padding: 1rem 1.25rem;
  }

  .login-logo-wrap {
    margin-bottom: 0.25rem;
  }

  .login-title {
    font-size: 1.1rem;
    margin-bottom: 0.15rem;
  }

  .login-subtitle {
    margin-bottom: 0.65rem;
    font-size: 0.8rem;
  }
}
</style>

<style>
/* el-dropdown popper is teleported to <body> */
.login-lang-dropdown-popper.el-popper {
  background: rgba(15, 20, 34, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
}

.login-lang-dropdown-popper .el-dropdown-menu {
  background: transparent !important;
  border: none !important;
  padding: 0.35rem 0;
}

.login-lang-dropdown-popper .el-dropdown-menu__item {
  color: #e8ecf4 !important;
  font-size: 0.9rem;
}

.login-lang-dropdown-popper .el-dropdown-menu__item:hover,
.login-lang-dropdown-popper .el-dropdown-menu__item:focus {
  background: rgba(99, 102, 241, 0.25) !important;
  color: #f8fafc !important;
}

.login-lang-dropdown-popper .el-popper__arrow::before {
  background: rgba(15, 20, 34, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
</style>
